import { AloftPrice, AloftPriceJSON } from '@aloft/toolkit-shared';
import { createSelector } from '@reduxjs/toolkit';

import {
  selectCalculatedMarketAdjustment,
  selectCalculatedMarketAdjustments,
} from './calculatedMarketAdjustmentSelectors/selectCalculatedMarketAdjustment.ts';
import { selectOpinionOfSiteValue } from './opinionOfSiteValueSelectors.ts';
import { selectCompSequence } from './orderSelectors.ts';
import { selectUnadjustedSiteCost, selectUnadjustedSiteCosts } from './selectUnadjustedSiteCost.ts';

type SiteValueParams = {
  opinionOfSiteValue: AloftPriceJSON;
  unadjustedSiteCost: ReturnType<typeof selectUnadjustedSiteCost>;
  marketAdjustment?: AloftPrice;
};

const calculateCompSiteValue = (
  unadjustedSiteCost: ReturnType<typeof selectUnadjustedSiteCost>,
  marketAdjustment?: AloftPrice,
): AloftPrice | undefined => {
  if (!unadjustedSiteCost || !marketAdjustment) return undefined;

  return unadjustedSiteCost.add(marketAdjustment);
};

const calculateSiteValueOuter = (
  propID: string,
  { opinionOfSiteValue, unadjustedSiteCost, marketAdjustment }: SiteValueParams,
) => {
  if (propID === 'subject' || propID === 'subjectArv') {
    return AloftPrice.schema.parse(opinionOfSiteValue);
  }

  return calculateCompSiteValue(unadjustedSiteCost, marketAdjustment);
};

export const selectSiteValue = createSelector(
  [
    selectUnadjustedSiteCost,
    selectCalculatedMarketAdjustment,
    selectOpinionOfSiteValue,
    (_state, id: string) => id,
  ],
  (unadjustedSiteCost, marketAdjustment, opinionOfSiteValue, id): AloftPrice | undefined =>
    calculateSiteValueOuter(id, {
      opinionOfSiteValue,
      unadjustedSiteCost,
      marketAdjustment,
    }),
);

export const selectSiteValues = createSelector(
  [
    selectUnadjustedSiteCosts,
    selectCalculatedMarketAdjustments,
    selectOpinionOfSiteValue,
    selectCompSequence,
  ],
  (unadjustedSiteCosts, marketAdjustments, opinionOfSiteValue, compSequence) => {
    const properties = ['subject', ...compSequence];

    return Object.fromEntries(
      properties.map((propID) => [
        propID,
        calculateSiteValueOuter(propID, {
          opinionOfSiteValue,
          unadjustedSiteCost: unadjustedSiteCosts?.[propID],
          marketAdjustment: marketAdjustments?.[propID],
        }),
      ]),
    );
  },
);

export const selectSiteValueDisplay = createSelector([selectSiteValue], (siteValue) =>
  siteValue?.toString(true),
);

export const selectSiteValuesDisplay = createSelector([selectSiteValues], (siteValues) =>
  siteValues
    ? Object.fromEntries(
        Object.entries(siteValues).map(([propID, siteValue]) => [
          propID,
          siteValue?.toString(true),
        ]),
      )
    : undefined,
);
